import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Users, Eye, TrendingUp, Award, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { AuthContext } from '@/App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    checkAdminAndFetchData();
  }, []);

  const checkAdminAndFetchData = async () => {
    try {
      // Check if user is admin
      const adminCheckRes = await axios.get(`${API}/admin/check`, { withCredentials: true });
      
      if (!adminCheckRes.data.is_admin) {
        toast.error('Access denied. Admin only.');
        navigate('/');
        return;
      }
      
      setIsAdmin(true);
      
      // Fetch analytics data
      const analyticsRes = await axios.get(`${API}/admin/analytics`, { withCredentials: true });
      setAnalytics(analyticsRes.data);
      
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        toast.error('Access denied. Admin only.');
        navigate('/');
      } else {
        toast.error('Failed to load analytics');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}}>
        <div className="text-white text-xl">Loading admin dashboard...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}}>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button onClick={() => navigate('/dashboard')} variant="ghost" className="text-white hover:bg-white/20">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="text-white text-right">
            <p className="text-sm opacity-80">Admin: {user?.email}</p>
          </div>
        </div>

        <h1 className="text-4xl font-bold text-white mb-2">Admin Analytics</h1>
        <p className="text-white/80 mb-8">Data since launch: {analytics?.launch_date ? formatDate(analytics.launch_date) : 'Nov 20, 2024'}</p>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Users */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <Users className="w-8 h-8 text-purple-600" />
              <span className="text-xs font-semibold text-gray-500 uppercase">Total Users</span>
            </div>
            <p className="text-4xl font-bold text-gray-800">{analytics?.total_users || 0}</p>
            <p className="text-sm text-gray-500 mt-2">Registered users</p>
          </div>

          {/* Total Page Views */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <Eye className="w-8 h-8 text-blue-600" />
              <span className="text-xs font-semibold text-gray-500 uppercase">Page Views</span>
            </div>
            <p className="text-4xl font-bold text-gray-800">{analytics?.total_page_views || 0}</p>
            <p className="text-sm text-gray-500 mt-2">Total footfall</p>
          </div>

          {/* Weekly Active Users */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className="w-8 h-8 text-green-600" />
              <span className="text-xs font-semibold text-gray-500 uppercase">Weekly Active</span>
            </div>
            <p className="text-4xl font-bold text-gray-800">{analytics?.weekly_active_users || 0}</p>
            <p className="text-sm text-gray-500 mt-2">Last 7 days</p>
          </div>

          {/* Engagement Rate */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <Award className="w-8 h-8 text-orange-600" />
              <span className="text-xs font-semibold text-gray-500 uppercase">Engagement</span>
            </div>
            <p className="text-4xl font-bold text-gray-800">
              {analytics?.total_users > 0 
                ? Math.round((analytics.weekly_active_users / analytics.total_users) * 100) 
                : 0}%
            </p>
            <p className="text-sm text-gray-500 mt-2">Weekly active rate</p>
          </div>
        </div>

        {/* Page Breakdown */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Eye className="w-6 h-6 text-purple-600" />
            Page-Wise Footfall
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Page Path</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Total Views</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Unique Visitors</th>
                </tr>
              </thead>
              <tbody>
                {analytics?.page_breakdown?.map((page, idx) => (
                  <tr key={idx} className="border-b border-gray-100 hover:bg-purple-50">
                    <td className="py-3 px-4 text-gray-800">{page._id || page.page_path || 'Unknown'}</td>
                    <td className="py-3 px-4 text-right font-semibold text-gray-800">{page.views}</td>
                    <td className="py-3 px-4 text-right text-gray-600">{page.unique_visitors}</td>
                  </tr>
                ))}
                {(!analytics?.page_breakdown || analytics.page_breakdown.length === 0) && (
                  <tr>
                    <td colSpan="3" className="py-8 text-center text-gray-500">
                      No page view data available yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Popular Events and Wishlists */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Popular Events */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Calendar className="w-6 h-6 text-purple-600" />
              Popular Events
            </h2>
            <div className="space-y-3">
              {analytics?.popular_events?.map((event, idx) => (
                <div key={event.id} className="p-4 bg-purple-50 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800">{idx + 1}. {event.name}</p>
                      <p className="text-sm text-gray-600">by {event.creator_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-purple-600">{event.item_count}</p>
                      <p className="text-xs text-gray-500">items</p>
                    </div>
                  </div>
                </div>
              ))}
              {(!analytics?.popular_events || analytics.popular_events.length === 0) && (
                <p className="text-center text-gray-500 py-8">No events yet</p>
              )}
            </div>
          </div>

          {/* Popular Wishlists */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Award className="w-6 h-6 text-purple-600" />
              Popular Wishlists
            </h2>
            <div className="space-y-3">
              {analytics?.popular_wishlists?.map((wishlist, idx) => (
                <div key={wishlist.id} className="p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800">{idx + 1}. {wishlist.name}</p>
                      <p className="text-sm text-gray-600">by {wishlist.creator_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-blue-600">{wishlist.item_count}</p>
                      <p className="text-xs text-gray-500">{wishlist.selected_items} selected</p>
                    </div>
                  </div>
                </div>
              ))}
              {(!analytics?.popular_wishlists || analytics.popular_wishlists.length === 0) && (
                <p className="text-center text-gray-500 py-8">No wishlists yet</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
